import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { SiaFileArchive } from '../interfaces/sia-file-archive';
import { SiaFileChunk } from '../interfaces/sia-file-chunk';
import { SiaArchivePath } from '../interfaces/sia-file-path';
import { SiaHttpService } from '../services/http.service';

@Injectable({
  providedIn: 'root',
})
export class FileApi {
  private http = inject(SiaHttpService);
  private readonly usersController: string = 'users';
  private readonly productsController: string = 'products';
  private readonly imagesController: string = 'images';
  private readonly configController: string = 'gridConfig';
  private readonly fileSystemController: string = 'file-system';

  startUpload(archive: SiaArchivePath, name: string): Promise<string> {
    return this.http.post(`${this.fileSystemController}/archive/upload/start`, {
      archive: archive,
      name: name,
    });
  }
  downloadPerspective(componentId: string, componentType: string) {
    const body = { componentId, componentType };
    const options = {
      responseType: 'blob' as const,
      observe: 'response' as const,
    };
    return this.http.post(`${this.fileSystemController}/download-perspective`, body, options);
  }
  blobToBase64 = (blob: any) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve(reader.result);
      };
    });
  };
  async sendFileChunk(fileHandle: string, chunk: Blob, offset: number) {
    let data = await this.blobToBase64(chunk);
    let base64 = typeof data === 'string' ? data : undefined;
    base64 = base64?.replace('data:application/octet-stream;base64,', '');
    let chunkData: SiaFileChunk = {
      fileHandle: fileHandle,
      data: base64,
      startAt: offset.toString(),
    };
    return this.http.post(`${this.fileSystemController}/upload/chunk`, chunkData);
  }

  endUpload(
    archive: SiaArchivePath,
    size: number,
    fileHandle: string,
    name: string,
    parentPath?: string,
    returnUrl?: boolean,
  ) {
    return this.http.post(`${this.fileSystemController}/archive/upload/end`, {
      archive: archive,
      name: name,
      size: size,
      fileHandle: fileHandle,
      parentPath: parentPath || '',
      returnUrl: returnUrl || false,
    });
  }

  deleteFile(path: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/archive-file/delete`, path);
  }

  getFilesNames(folderName: string, isRelative: boolean): Promise<string[]> {
    var params = { folderPath: folderName, isRelative: isRelative };
    return this.http.post(`${this.fileSystemController}/file-names`, params);
  }
  async moveFileAndDelete(move: string, to: string, deleteFile: string, isRelative: boolean) {
    var params = {
      move: move,
      to: to,
      delete: deleteFile,
      isRelative: isRelative,
    };
    return this.http.post(`${this.fileSystemController}/move-delete`, params);
  }

  downloadFilesAsZip(folderPath: string, isRelative: boolean) {
    return this.http.post(`${this.fileSystemController}/file-list`, {
      folderPath: folderPath,
      isRelative: isRelative,
    });
  }

  getFileNames(folderName: string, filterExtensionsArr?: string[]) {
    // var params = new HttpParams();
    // params = params.set('folderName', `${folder}`);
    // params = params.set('filterExtensions', `${filterExtensions}`);
    let filterExtensions = filterExtensionsArr;
    return this.http.post(`${this.fileSystemController}/list-files`, {
      folderName,
      filterExtensions,
    });
  }

  getArchives(key: string): Promise<SiaFileArchive[]> {
    return this.http.get(`${this.fileSystemController}/archives/${key}`, {});
  }
  getArchive(id: string): Promise<SiaFileArchive | null> {
    return this.http.get(`${this.fileSystemController}/archive/${id}`, {});
  }

  pathHasContent(archivePath: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/path-has-content`, archivePath);
  }

  getSystemArchives(): Promise<SiaFileArchive[]> {
    return this.http.get(`${this.fileSystemController}/system-archives`, {});
  }

  getBase64File(file: SiaArchivePath): Promise<string> {
    return this.http.post(`${this.fileSystemController}/base64-file`, file);
  }

  saveBase64File(file: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/save-base64-file`, file);
  }

  getFiles(path: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/elements`, path);
  }
  getSystemFiles(path: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/system-elements`, path);
  }
  getFileTree(path: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/archive-file-tree`, path);
  }
  downloadFile(path: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/archive-file/download`, path, {
      responseType: 'blob',
    });
  }

  renameFile(file: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/archive-file/rename`, file);
  }

  // overloads provided above

  createFolder(archive: any, name: string): Promise<any> {
    return this.http.post(`${this.fileSystemController}/archive/upload/folder`, {
      archive,
      name,
    });
  }

  move(name: string, moveToFolder: string, parent: string, archive: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/archive-file/move`, {
      fileToMove: name,
      folderToMoveIn: moveToFolder,
      parent: parent,
      archive: archive,
    });
  }

  moveUp(name: string, parent: string, archive: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/archive-file/move-up`, {
      fileToMove: name,
      parent: parent,
      archive: archive,
    });
  }

  moveAll(tmpPath: SiaArchivePath, finalPath: SiaArchivePath) {
    return this.http.post(`${this.fileSystemController}/archive/move-all`, {
      tmpArchive: tmpPath,
      archive: finalPath,
    });
  }

  //#region private methods
  private execDownload(controller: string, params: any) {
    return this.http.get(controller, {
      responseType: 'blob',
      params: params,
    });
  }
  private execUpload(controller: string, file: any) {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', []);
    let options = { headers: headers };
    return this.http.post(controller, file, options);
  }

  //#endregion

  //#region public methods
  uploadAvatarImage(form: any) {
    let controller = `${this.usersController}/avatar`;
    return this.execUpload(controller, form);
  }

  getThumbnailById(id: string) {
    let params = new HttpParams();
    params = params.set('id', `${id}`);
    let controller = `${this.productsController}/thumbnail`;
    return this.execDownload(controller, params);
  }

  getHighResolutionById(id: string) {
    let params = new HttpParams();
    params = params.set('id', `${id}`);
    let controller = `${this.productsController}/hr`;
    return this.execDownload(controller, params);
  }

  getPoseById(id: string, index: number) {
    let params = new HttpParams();
    params = params.set('id', `${id}`);
    params = params.set('index', `${index}`);
    let controller = `${this.productsController}/pose`;
    return this.execDownload(controller, params);
  }

  getPosesCountById(id: string) {
    let params = new HttpParams();
    params = params.set('id', `${id}`);
    let controller = `${this.productsController}/poses-count`;
    return this.http.get(controller, {
      params,
    });
  }

  getWallpaper() {
    let controller = `${this.imagesController}/wallpaper`;
    return this.execDownload(controller, {});
  }

  getAvatar(uid: string) {
    let params = new HttpParams();
    params = params.set('avatarId', `${uid}`);
    let controller = `${this.usersController}/avatar`;
    return this.execDownload(controller, params);
  }

  uploadGridConfig(form: any) {
    let controller = `${this.configController}/grid-conf-fs-save`;
    return this.execUpload(controller, form);
  }

  getCustomerIcon() {
    let controller = `${this.imagesController}/customericon`;
    return this.execDownload(controller, {});
  }

  //#endregion
}
